import type { EditorState, ExportFormat, Ratio, TextLayer } from './types';

/** Canonical pixel grid per ratio — also the default export size. */
export const DESIGN: Record<Ratio, { width: number; height: number }> = {
  '16:9': { width: 1280, height: 720 },
  '9:16': { width: 1080, height: 1920 },
};

export const EXPORT_SIZES: Record<Ratio, { label: string; width: number; height: number }[]> = {
  '16:9': [
    { label: '1280 × 720 (YouTube 권장)', width: 1280, height: 720 },
    { label: '1920 × 1080 (Full HD)', width: 1920, height: 1080 },
    { label: '2560 × 1440 (2K)', width: 2560, height: 1440 },
  ],
  '9:16': [
    { label: '1080 × 1920 (Shorts 권장)', width: 1080, height: 1920 },
    { label: '720 × 1280', width: 720, height: 1280 },
    { label: '1440 × 2560', width: 1440, height: 2560 },
  ],
};

export const EXPORT_FORMATS: { value: ExportFormat; label: string; mime: string; ext: string; lossy: boolean }[] = [
  { value: 'png', label: 'PNG', mime: 'image/png', ext: 'png', lossy: false },
  { value: 'jpeg', label: 'JPEG', mime: 'image/jpeg', ext: 'jpg', lossy: true },
  { value: 'webp', label: 'WebP', mime: 'image/webp', ext: 'webp', lossy: true },
];

export interface FontOption {
  label: string;
  value: string;
  /** Weights the webfont actually ships; used to clamp the weight control. */
  weights: number[];
}

export const KOREAN_FONTS: FontOption[] = [
  { label: 'Noto Sans KR', value: 'Noto Sans KR', weights: [400, 700, 900] },
  { label: 'Pretendard', value: 'Pretendard', weights: [400, 700, 900] },
  { label: '나눔고딕', value: 'Nanum Gothic', weights: [400, 700, 800] },
  { label: '나눔명조', value: 'Nanum Myeongjo', weights: [400, 700, 800] },
  { label: '나눔펜스크립트', value: 'Nanum Pen Script', weights: [400] },
  { label: '나눔브러시', value: 'Nanum Brush Script', weights: [400] },
  { label: '나눔고딕코딩', value: 'Nanum Gothic Coding', weights: [400, 700] },
  { label: '나눔스퀘어', value: 'NanumSquare', weights: [400, 700, 800] },
  { label: 'Spoqa Han Sans Neo', value: 'Spoqa Han Sans Neo', weights: [400, 700] },
  { label: 'G마켓 산스', value: 'GmarketSans', weights: [400, 700] },
];

export const ENGLISH_FONTS: FontOption[] = [
  { label: 'Roboto', value: 'Roboto', weights: [400, 700, 900] },
  { label: 'Open Sans', value: 'Open Sans', weights: [400, 700, 800] },
  { label: 'Lato', value: 'Lato', weights: [400, 700, 900] },
  { label: 'Montserrat', value: 'Montserrat', weights: [400, 700, 900] },
  { label: 'Poppins', value: 'Poppins', weights: [400, 700, 900] },
  { label: 'Source Sans 3', value: 'Source Sans 3', weights: [400, 700, 900] },
  { label: 'Raleway', value: 'Raleway', weights: [400, 700, 900] },
  { label: 'Ubuntu', value: 'Ubuntu', weights: [400, 700] },
  { label: 'Playfair Display', value: 'Playfair Display', weights: [400, 700, 900] },
  { label: 'Merriweather', value: 'Merriweather', weights: [400, 700, 900] },
];

export const ALL_FONTS = [...KOREAN_FONTS, ...ENGLISH_FONTS];

export function fontWeightsFor(family: string): number[] {
  return ALL_FONTS.find((f) => f.value === family)?.weights ?? [400, 700];
}

const MAIN_DEFAULT: TextLayer = {
  text: '',
  fontFamily: 'Noto Sans KR',
  fontSize: 128,
  fontWeight: 900,
  color: '#ffffff',
  align: 'center',
  x: 0,
  y: -56,
  lineHeight: 1.2,
  letterSpacing: 0,
  strokeEnabled: false,
  strokeWidth: 6,
  strokeColor: '#000000',
  shadowEnabled: true,
  shadowBlur: 24,
  shadowColor: '#000000',
  backgroundEnabled: false,
  backgroundColor: '#000000',
  backgroundOpacity: 40,
  backgroundPadding: 24,
  backgroundRadius: 16,
};

const SUB_DEFAULT: TextLayer = {
  ...MAIN_DEFAULT,
  fontSize: 56,
  fontWeight: 700,
  y: 112,
  shadowBlur: 16,
  strokeWidth: 3,
  backgroundPadding: 16,
  backgroundRadius: 12,
};

export const INITIAL_STATE: EditorState = {
  ratio: '16:9',
  backgroundColor: '#1a0033',
  main: { ...MAIN_DEFAULT },
  sub: { ...SUB_DEFAULT },
  filters: {
    brightness: 100,
    contrast: 100,
    saturate: 100,
    blur: 0,
    overlayColor: '#000000',
    overlayOpacity: 30,
  },
  frame: {
    enabled: true,
    width: 20,
    color: '#a259c6',
    radius: 56,
  },
};

/** Ready-made looks. Applied as a style-only patch, so text stays put. */
export const TEMPLATES: { id: string; name: { ko: string; en: string }; patch: Partial<EditorState> }[] = [
  {
    id: 'impact',
    name: { ko: '볼드 임팩트', en: 'Bold Impact' },
    patch: {
      main: { ...MAIN_DEFAULT, fontSize: 150, strokeEnabled: true, strokeWidth: 10, shadowBlur: 10 },
      sub: { ...SUB_DEFAULT, fontSize: 60, strokeEnabled: true, strokeWidth: 4 },
      filters: { brightness: 95, contrast: 115, saturate: 110, blur: 0, overlayColor: '#000000', overlayOpacity: 25 },
      frame: { enabled: true, width: 20, color: '#ffd400', radius: 24 },
    },
  },
  {
    id: 'minimal',
    name: { ko: '미니멀', en: 'Minimal' },
    patch: {
      main: { ...MAIN_DEFAULT, fontFamily: 'Pretendard', fontSize: 104, fontWeight: 700, letterSpacing: -2, shadowEnabled: false },
      sub: { ...SUB_DEFAULT, fontFamily: 'Pretendard', fontSize: 44, fontWeight: 400, shadowEnabled: false, y: 72 },
      filters: { brightness: 100, contrast: 100, saturate: 100, blur: 0, overlayColor: '#000000', overlayOpacity: 15 },
      frame: { enabled: false, width: 0, color: '#ffffff', radius: 0 },
    },
  },
  {
    id: 'caption',
    name: { ko: '자막 박스', en: 'Caption Box' },
    patch: {
      main: { ...MAIN_DEFAULT, fontSize: 112, backgroundEnabled: true, backgroundOpacity: 70, shadowEnabled: false, y: -24 },
      sub: { ...SUB_DEFAULT, fontSize: 48, backgroundEnabled: true, backgroundOpacity: 55, shadowEnabled: false, y: 104 },
      filters: { brightness: 90, contrast: 100, saturate: 95, blur: 2, overlayColor: '#000000', overlayOpacity: 20 },
      frame: { enabled: true, width: 12, color: '#ffffff', radius: 32 },
    },
  },
  {
    id: 'neon',
    name: { ko: '네온', en: 'Neon' },
    patch: {
      main: { ...MAIN_DEFAULT, color: '#7ef9ff', shadowColor: '#0066ff', shadowBlur: 48, strokeEnabled: true, strokeWidth: 3, strokeColor: '#001a4d' },
      sub: { ...SUB_DEFAULT, color: '#ff7ee6', shadowColor: '#a259c6', shadowBlur: 32 },
      filters: { brightness: 80, contrast: 125, saturate: 130, blur: 1, overlayColor: '#1a0044', overlayOpacity: 40 },
      frame: { enabled: true, width: 16, color: '#7ef9ff', radius: 48 },
    },
  },
];

export const LIMITS = {
  mainTextMaxLength: 80,
  subTextMaxLength: 140,
  fontSize: { min: 16, max: 400 },
  strokeWidth: { min: 1, max: 24 },
  blur: { min: 0, max: 40 },
  frameWidth: { min: 0, max: 120 },
  frameRadius: { min: 0, max: 200 },
  padding: { min: 0, max: 120 },
  letterSpacing: { min: -20, max: 40 },
  lineHeight: { min: 0.8, max: 2.5 },
  /** Guard rail on uploads; 25 MB is far past anything a thumbnail needs. */
  maxImageBytes: 25 * 1024 * 1024,
  historyLimit: 80,
};

export const STORAGE_KEYS = {
  editor: 'thumbnail-generator:editor:v1',
  presets: 'thumbnail-generator:presets:v1',
  language: 'thumbnail-generator:language:v1',
  exportOptions: 'thumbnail-generator:export:v1',
};
